import { buttonVariants } from '@/components/shadcn/button'
import { AuiIf, ThreadPrimitive } from '@assistant-ui/react'
import { useEffect, useRef } from 'react'
import { DmpChatMessage } from './DmpChatMessage'
import { DmpChatComposer } from './DmpChatComposer'
import type { DmpChatComposerInputHandle } from './DmpChatLexicalInput'
import { useDmpChatRuntime } from './DmpChatRuntimeProvider'

export function DmpChatThread({ projectName }: { projectName: string }) {
  const {
    activeTurn,
    canRetry,
    connectionError,
    connectionStatus,
    isHydrating,
    isRecovering,
    isTurnActive,
    retryLastTurn,
    terminalOutcome,
  } = useDmpChatRuntime()
  const inputRef = useRef<DmpChatComposerInputHandle>(null)
  const wasTurnActiveRef = useRef(false)

  useEffect(() => {
    const wasTurnActive = wasTurnActiveRef.current
    wasTurnActiveRef.current = isTurnActive
    if (!wasTurnActive || isTurnActive) {
      return
    }

    const focusFrame = window.requestAnimationFrame(() => {
      inputRef.current?.focus()
    })
    return () => window.cancelAnimationFrame(focusFrame)
  }, [isTurnActive])

  return (
    <ThreadPrimitive.Root
      aria-label={`DMP Chat for ${projectName}`}
      className="flex min-h-0 min-w-0 flex-1 flex-col gap-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 px-1">
        <div>
          <h2 className="text-lg font-semibold">DMP Chat</h2>
          <p className="text-sm text-muted-foreground">
            Ask the Project Agent about <span className="font-medium text-foreground">{projectName}</span>.
          </p>
        </div>
      </div>

      <div
        id="dmp-chat-turn-status"
        aria-atomic="true"
        aria-live="polite"
        className="min-h-5 px-1 text-sm text-muted-foreground"
        role="status"
      >
        {isRecovering && 'Reconnected. Checking the task result; missed live events were not replayed.'}
        {!isRecovering && isHydrating && 'Resuming the selected Thread…'}
        {!isRecovering && !activeTurn && connectionStatus === 'mock' && 'Mock response mode — no agent connection.'}
        {!isRecovering && connectionStatus === 'reconnecting' && 'Connection lost. Reconnecting to the Project Agent…'}
        {!isRecovering && !activeTurn && connectionStatus === 'connecting' && 'Connecting to the Project Agent…'}
        {!isRecovering &&
          !connectionError &&
          activeTurn?.status === 'starting' &&
          activeTurn.task.status === 'creating' &&
          'Joining the Project Agent thread…'}
        {!isRecovering &&
          !connectionError &&
          activeTurn?.status === 'starting' &&
          activeTurn.task.status !== 'creating' &&
          'Starting the Project Agent…'}
        {!isRecovering && !connectionError && activeTurn?.status === 'running' && 'Project Agent is responding…'}
        {!isRecovering &&
          !connectionError &&
          activeTurn?.status === 'cancellation-requested' &&
          'Cancelling the request…'}
        {!isRecovering && connectionError}
        {!isRecovering && !activeTurn && !connectionError && terminalOutcome?.summary}
      </div>
      {canRetry && (
        <div className="flex justify-start px-1">
          <button
            aria-describedby="dmp-chat-turn-status"
            aria-label="Retry the last Project Agent request"
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
            onClick={retryLastTurn}
            type="button"
          >
            Retry request
          </button>
        </div>
      )}

      <ThreadPrimitive.Viewport
        className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain rounded-lg bg-offwhite lg:px-20 xl:px-40"
        autoScroll
      >
        <AuiIf condition={({ thread }) => thread.isEmpty}>
          <div className="flex h-full min-h-64 items-center justify-center px-6 py-12 text-center">
            <div className="flex max-w-lg flex-col gap-2">
              <p className="text-sm text-muted-foreground">
                Ask the Project Agent about <span className="font-medium text-foreground">{projectName}</span>.
              </p>
            </div>
          </div>
        </AuiIf>
        <ThreadPrimitive.Messages>{({ message }) => <DmpChatMessage key={message.id} />}</ThreadPrimitive.Messages>
      </ThreadPrimitive.Viewport>

      <DmpChatComposer inputRef={inputRef} projectName={projectName} />
    </ThreadPrimitive.Root>
  )
}
